import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  notification,
  Spin,
  Space,
} from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import SimplestTable from "../components/simplestTable";
import { AppContext } from "../context/AppContext";

const { Option } = Select;

const Users = () => {
  const token = localStorage.getItem("token");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const { company } = useContext(AppContext);
  // Add these state variables alongside your other state hooks
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const showDeleteModal = (userId) => {
    console.log("Preparing to delete user with ID:", userId); // Debug log
    setDeletingUserId(userId);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    console.log("Attempting to delete user with ID:", deletingUserId); // Debug log
    try {
      await axios.delete(
        `https://zedlenders.pickmesms.com/users/${deletingUserId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notification.success({
        message: "Success",
        description: "User deleted successfully",
      });
      fetchUsers(); // Refresh the user list
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to delete user",
      });
    } finally {
      setDeleteModalVisible(false);
      setDeletingUserId(null);
    }
  };

  const columns = [
    {
      title: "User",
      render: (_, record) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: "Role",
      render: (_, record) => record.role?.name,
    },
    {
      title: "Role Description",
      render: (_, record) => record.role?.description,
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span style={{ color: status === "active" ? "green" : "red" }}>
          {status.toUpperCase()}
        </span>
      ),
    },
    {
      title: (
        <span style={{ color: '#1890ff' }}>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          Last Logged In
        </span>
      ),
      dataIndex: "last_access",
      render: (lastAccess) => {
        if (!lastAccess) {
          return (
            <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              Never
            </span>
          );
        }
        const date = new Date(lastAccess);
        const now = new Date();
        const diffHours = Math.abs(now - date) / 36e5;
        
        const isRecent = diffHours < 24;
        const color = isRecent ? '#52c41a' : '#faad14';
        
        return (
          <span style={{ color: color, fontWeight: '500' }}>
            <ClockCircleOutlined style={{ marginRight: 6 }} />
            {date.toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        );
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          {/* <Button danger onClick={() => showDeleteModal(record.id)}>
            Delete
          </Button> */}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_neq]=Borrower&filter[company][_eq]=${company.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(response.data.data);
    } catch (err) {
      notification.error({
        message: "Error",
        description: "Failed to fetch users",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/roles?filter[name][_neq]=Borrower`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRoles(response.data.data);
    } catch (err) {
      notification.error({
        message: "Error",
        description: "Failed to fetch roles",
      });
    }
  };

  const handleCreate = () => {
    form.resetFields();
    setEditingUser(null);
    setIsModalVisible(true);
  };

  const handleEdit = (user) => {
    form.setFieldsValue({
      ...user,
      role: user.role?.id,
    });
    setEditingUser(user);
    setIsModalVisible(true);
  };

  const handleDelete = (userId) => {
    console.log("Deleting user with ID:", userId); // Debug log
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this user?",
      async onOk() {
        console.log("Attempting to delete user with ID:", userId); // Debug log
        try {
          // Return the axios promise explicitly
          await axios.delete(
            `https://zedlenders.pickmesms.com/users/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          notification.success({
            message: "Success",
            description: "User deleted successfully",
          });
          fetchUsers(); // Refresh the user list
        } catch (err) {
          console.error("Error deleting user:", err); // Log error for debugging
          notification.error({
            message: "Error",
            description: err.response?.data?.message || "Failed to delete user",
          });
          // Throw error to prevent modal from auto-closing if needed
          throw err;
        }
      },
      okText: "Yes",
      cancelText: "No",
    });
  };

  const handleSubmit = async (values) => {
    let payload = editingUser
      ? values
      : { ...values, password: "123456", needs_password_change: true, company: company.id };

    try {
      const url = editingUser
        ? `https://zedlenders.pickmesms.com/users/${editingUser.id}`
        : `https://zedlenders.pickmesms.com/users`;

      const method = editingUser ? "patch" : "post";

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      notification.success({
        message: "Success",
        description: `User ${editingUser ? "updated" : "created"} successfully`,
      });

      setIsModalVisible(false);
      fetchUsers();
    } catch (err) {
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "An error occurred",
      });
    }
  };

  return (
    <div className="row">
      <div className="col-md-12">
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={handleCreate}>
            Add User
          </Button>
        </div>

        <SimplestTable
          data={users}
          columns={columns}
          tableName="Users"
          pageSize={5}
          width="col-md-12"
          loading={loading}
          onRefresh={fetchUsers}
        />

        <Modal
          title={editingUser ? "Edit User" : "Create User"}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, type: "email" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label="Role" name="role" rules={[{ required: true }]}>
              <Select>
                {roles.map((role) => (
                  <Option key={role.id} value={role.id}>
                    {role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Confirm Delete"
          visible={deleteModalVisible}
          onOk={handleDeleteConfirm}
          onCancel={() => setDeleteModalVisible(false)}
          okText="Yes"
          cancelText="No"
        >
          <p>Are you sure you want to delete this user?</p>
        </Modal>
      </div>
    </div>
  );
};

export default Users;
